import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Heading,
  Text,
  Button,
  useToast,
  useDisclosure,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { EditIcon, SettingsIcon } from '@chakra-ui/icons';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import EditProjectModal from './EditProjectModal';
import AIPromptModal from './AIPromptModal';
import FloorPlanCanvas from '../canvas/FloorPlanCanvas';
import ThreeDViewer from '../canvas/ThreeDViewer';
import ReactPlannerViewer from '../canvas/ReactPlannerViewer';
import ProductLibrary from './ProductLibrary';
import ProjectSidebar from './ProjectSidebar';
import ProductSidebar from './ProductSidebar';

const ProjectView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [activeView, setActiveView] = useState('2d'); // '2d', '3d', or 'react-planner'
  const [project, setProject] = useState(null);
  const [floorPlanData, setFloorPlanData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProductLibraryOpen, setIsProductLibraryOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isProductSidebarOpen, setIsProductSidebarOpen] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  
  // View IDs for API calls
  const [d2ViewId, setD2ViewId] = useState(null);
  const [d3ViewId, setD3ViewId] = useState(null);
  
  // Canvas state
  const [selectedTool, setSelectedTool] = useState('select');
  const [selectedObject, setSelectedObject] = useState(null);
  const [roomDimensions, setRoomDimensions] = useState({ width: 300, height: 200 });
  const [wallLength, setWallLength] = useState(200);
  const [windowLength, setWindowLength] = useState(120); // 10ft window (10 * 12 pixels)
  const [doorLength, setDoorLength] = useState(80);
  const [draftObjectsCount, setDraftObjectsCount] = useState(0);
  
  // Canvas refs
  const canvasRef = useRef(null);
  const threeDRef = useRef(null);
  const reactPlannerRef = useRef(null);
  
  // Modal states
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();

  // Load project data
  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/v1/projects/${id}`);
      setProject(response.data.project);
      
      // Load floor plan data and view IDs if exists
      if (response.data.project.project_views && response.data.project.project_views.length > 0) {
        const d2View = response.data.project.project_views.find(view => view.project_view.view_type === 'd2');
        if (d2View) {
          setFloorPlanData(d2View.project_view.data);
          setD2ViewId(d2View.project_view.id);
        }
        
        const d3View = response.data.project.project_views.find(view => view.project_view.view_type === 'd3');
        if (d3View) {
          setD3ViewId(d3View.project_view.id);
        }
      }
    } catch (error) {
      toast({
        title: 'Error loading project',
        description: 'Failed to load project data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate('/projects');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditProject = (updatedProject) => {
    setProject(updatedProject);
    toast({
      title: 'Project updated successfully',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Handle AI-generated objects
  const handleAIGeneratedObjects = useCallback(async (aiObjects) => {
    if (!aiObjects || !Array.isArray(aiObjects)) {
      console.error('Invalid AI objects received:', aiObjects);
      return;
    }
    setFloorPlanData({objects: aiObjects});
  }, [toast]);

  const handleSaveFloorPlan = useCallback(async (canvasData) => {
    if (!project || !d2ViewId) return;

    try {
      // Log the data being sent to API
      console.log('Sending canvas data to API:', canvasData);
      console.log('Project ID:', project.id);
      console.log('D2 View ID:', d2ViewId);
      
      // Update the existing 2D view with the complete canvas data
      const response = await api.put(`/api/v1/projects/${project.id}/project_views/${d2ViewId}`, {
        project_view: {
          data: canvasData,
          version: 1
        }
      });
      
      console.log('API Response:', response);
      
      toast({
        title: 'Floor plan saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error saving floor plan:', error);
      toast({
        title: 'Error saving floor plan',
        description: 'Failed to save floor plan data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [project, d2ViewId, toast]);

  const handleManualSave = useCallback(async () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.saveCanvas();
    } else if (activeView === 'react-planner' && reactPlannerRef.current) {
      reactPlannerRef.current.saveScene();
    }
  }, [activeView]);

  const handleProductSelect = (product) => {
    // Add product to canvas
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.addProduct(product);
    }
    
    toast({
      title: 'Product selected',
      description: `${product.name} added to floor plan`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    setIsProductLibraryOpen(false);
  };

  const handleAddProduct = () => {
    setIsProductSidebarOpen(true);
  };

  const handleJumpToView = (viewType) => {
    setActiveView(viewType);
    
    // If switching to 3D view, refresh the 3D scene with current 2D data
    if (viewType === '3d' && threeDRef.current) {
      // The ThreeDViewer will automatically update when floorPlanData changes
    }
    
    // If switching to React Planner view, initialize with current data
    if (viewType === 'react-planner' && reactPlannerRef.current) {
      // The ReactPlannerViewer will automatically update when floorPlanData changes
    }
  };

  // Sidebar action handlers
  const handleToolChange = (tool) => {
    setSelectedTool(tool);
    
    // Call the actual object creation methods for specific tools
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.setSelectedTool(tool);
      
      // Trigger object creation for specific tools
      if (tool === 'room') {
        canvasRef.current.addRoom();
      } else if (tool === 'window') {
        canvasRef.current.addWindow();
      } else if (tool === 'door') {
        canvasRef.current.addDoor();
      }
    }
  };

  const handleDeleteSelected = () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.deleteSelected();
    }
  };

  const handleSave = () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.saveCanvas();
    }
  };

  const handleClear = () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.clearCanvas();
    }
  };

  const handleUndo = () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.undo();
    }
  };

  const handleRedo = () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.redo();
    }
  };

  const handleZoomIn = () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.zoomIn();
    } else if (activeView === '3d' && threeDRef.current) {
      threeDRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.zoomOut();
    } else if (activeView === '3d' && threeDRef.current) {
      threeDRef.current.zoomOut();
    }
  };

  const handleReset = () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.resetView();
    } else if (activeView === '3d' && threeDRef.current) {
      threeDRef.current.resetCamera();
    }
  };

  const handleResetBottom = () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.resetView();
    } else if (activeView === '3d' && threeDRef.current) {
      threeDRef.current.resetCameraFromBottom();
    }
  };

  const handleRoomDimensionsChange = (dims) => {
    setRoomDimensions(dims);
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.setRoomDimensions(dims);
    }
  };

  const handleWallLengthChange = (length) => {
    setWallLength(length);
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.setWallLength(length);
    }
  };

  const handleWindowLengthChange = (length) => {
    setWindowLength(length);
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.setWindowLength(length);
    }
  };

  const handleDoorLengthChange = (length) => {
    setDoorLength(length);
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.setDoorLength(length);
    }
  };

  // Update selected object from canvas
  useEffect(() => {
    if (canvasRef.current) {
      // Listen for selection changes from canvas
      const updateSelectedObject = () => {
        if (canvasRef.current && canvasRef.current.selectedObject !== undefined) {
          setSelectedObject(canvasRef.current.selectedObject);
        }
      };
      
      // Set up interval to check for changes
      const interval = setInterval(updateSelectedObject, 100);
      return () => clearInterval(interval);
    }
  }, [canvasRef.current]);

  const handleClearDrafts = () => {
    if (activeView === '2d' && canvasRef.current) {
      canvasRef.current.clearDrafts();
      setDraftObjectsCount(0);
    }
  };

  if (isLoading) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <Text>Loading project...</Text>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <Text>Project not found</Text>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Subheader */}
      <Box bg="white" borderBottom="1px" borderColor="gray.200" px={6} py={4}>
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="md">{project.name}</Heading>
            <Text color="gray.600">{project.description}</Text>
          </VStack>

          <HStack spacing={4}>
            <Button
              variant="outline"
              colorScheme="blue"
              leftIcon={<EditIcon />}
              onClick={onEditModalOpen}
            >
              Edit Project
            </Button>
            
            <Button
              variant={activeView === '2d' ? 'solid' : 'ghost'}
              colorScheme="blue"
              onClick={() => handleJumpToView('2d')}
            >
              2D View
            </Button>
            
            <Button
              variant={activeView === '3d' ? 'solid' : 'ghost'}
              colorScheme="green"
              onClick={() => handleJumpToView('3d')}
            >
              3D View
            </Button>
            
            <Button
              variant={activeView === 'react-planner' ? 'solid' : 'ghost'}
              colorScheme="purple"
              onClick={() => handleJumpToView('react-planner')}
            >
              React Planner
            </Button>
            
            <Button
              variant="solid"
              colorScheme="blue"
              onClick={handleManualSave}
            >
              Save
            </Button>
            
            <Button
              variant="ghost"
              onClick={() => navigate('/projects')}
            >
              Back to Projects
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Main Content */}
      <Box position="relative" height="calc(100vh - 120px)">
        {/* Sidebar */}
        <ProjectSidebar
          selectedTool={selectedTool}
          onToolChange={handleToolChange}
          onDeleteSelected={handleDeleteSelected}
          onSave={handleSave}
          onClear={handleClear}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onReset={handleReset}
          onResetBottom={handleResetBottom}
          roomDimensions={roomDimensions}
          onRoomDimensionsChange={handleRoomDimensionsChange}
          wallLength={wallLength}
          onWallLengthChange={handleWallLengthChange}
          windowLength={windowLength}
          onWindowLengthChange={handleWindowLengthChange}
          doorLength={doorLength}
          onDoorLengthChange={handleDoorLengthChange}
          selectedObject={selectedObject}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          draftObjectsCount={draftObjectsCount}
          onClearDrafts={handleClearDrafts}
          onAddProduct={handleAddProduct}
        />

        {/* Canvas Container */}
        <Box
          position="absolute"
          left={isSidebarCollapsed ? "50px" : "300px"}
          right={0}
          top={0}
          bottom={0}
          p={4}
          transition="left 0.3s ease"
        >
          {activeView === '2d' ? (
            <FloorPlanCanvas
              ref={canvasRef}
              projectId={project.id}
              d2ViewId={d2ViewId}
              onSave={handleSaveFloorPlan}
              initialData={floorPlanData}
              sidebarWidth={300}
              isSidebarCollapsed={isSidebarCollapsed}
            />
          ) : activeView === '3d' ? (
            <ThreeDViewer
              ref={threeDRef}
              floorPlanData={floorPlanData}
              onUpdate={handleSaveFloorPlan}
              sidebarWidth={300}
              isSidebarCollapsed={isSidebarCollapsed}
            />
          ) : (
            <ReactPlannerViewer
              ref={reactPlannerRef}
              floorPlanData={floorPlanData}
              onUpdate={handleSaveFloorPlan}
              sidebarWidth={300}
              isSidebarCollapsed={isSidebarCollapsed}
            />
          )}
        </Box>
        
        {/* AI Floating Button - Only show in 2D view */}
        {activeView === '2d' && (
          <Tooltip label="Generate floor plan with AI" placement="left">
            <IconButton
              icon={<SettingsIcon />}
              colorScheme="blue"
              size="lg"
              isRound
              position="fixed"
              bottom="20px"
              right="20px"
              zIndex={1000}
              shadow="lg"
              onClick={() => setIsAIModalOpen(true)}
              aria-label="AI Floor Plan Generator"
              _hover={{
                transform: 'scale(1.05)',
                shadow: 'xl'
              }}
              transition="all 0.2s"
            />
          </Tooltip>
        )}
      </Box>

      {/* Modals */}
      <EditProjectModal
        isOpen={isEditModalOpen}
        onClose={onEditModalClose}
        project={project}
        onSuccess={handleEditProject}
      />

      <AIPromptModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        onSuccess={handleAIGeneratedObjects}
      />

      {/* Product Library Sidebar */}
      <ProductLibrary
        isOpen={isProductLibraryOpen}
        onClose={() => setIsProductLibraryOpen(false)}
        onProductSelect={handleProductSelect}
      />

      {/* Product Sidebar */}
      <ProductSidebar
        isOpen={isProductSidebarOpen}
        onClose={() => setIsProductSidebarOpen(false)}
        onProductSelect={handleProductSelect}
      />
    </Box>
  );
};

export default ProjectView; 